import { MapPin, Phone, Mail, Facebook, Instagram, Clock, ExternalLink } from "lucide-react";
import { restaurantConfig } from "../config/restaurantConfig";
import Reveal from "../components/Reveal";
import SectionEyebrow from "../components/SectionEyebrow";

const SOCIAL_ICONS = { facebook: Facebook, instagram: Instagram };

export default function Contact() {
  const { contact, social, hours, map, ui } = restaurantConfig;
  const t = ui.contact;
  return (
    <div data-testid="page-contact" className="pb-0">
      <section className="pt-12 md:pt-20 pb-12 text-center">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10">
          <Reveal>
            <SectionEyebrow className="justify-center inline-flex">{t.eyebrow}</SectionEyebrow>
            <h1 className="font-serif text-5xl sm:text-6xl md:text-7xl mt-6 italic whitespace-pre-line" style={{ fontWeight: 400 }}>
              {t.title}
            </h1>
            <p className="mt-6 max-w-xl mx-auto text-muted-foreground font-sans font-light">
              {t.subtitle}
            </p>
          </Reveal>
        </div>
      </section>

      {/* Info grid */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
        <Reveal>
          <InfoCard
            icon={MapPin}
            title={t.cards.address.title}
            primary={contact.addressLine1}
            secondary={contact.addressLine2}
            ctaLabel={t.cards.address.cta}
            ctaHref={map.locationUrl}
            external
            testid="contact-card-address"
          />
        </Reveal>
        <Reveal delay={0.1}>
          <InfoCard
            icon={Phone}
            title={t.cards.phone.title}
            primary={contact.phoneDisplay}
            secondary={t.cards.phone.secondary}
            ctaLabel={t.cards.phone.cta}
            ctaHref={contact.phoneLink}
            testid="contact-card-phone"
          />
        </Reveal>
        <Reveal delay={0.2}>
          <InfoCard
            icon={Mail}
            title={t.cards.email.title}
            primary={contact.email}
            secondary={t.cards.email.secondary}
            ctaLabel={t.cards.email.cta}
            ctaHref={`mailto:${contact.email}`}
            testid="contact-card-email"
          />
        </Reveal>
      </section>

      {/* Hours + social */}
      <section className="max-w-[1400px] mx-auto px-6 md:px-10 mt-16 md:mt-24 grid grid-cols-1 md:grid-cols-2 gap-12 lg:gap-20">
        <Reveal>
          <SectionEyebrow>{t.hoursTitle}</SectionEyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl mt-5 italic" style={{ fontWeight: 400 }}>
            {t.hoursHeading}
          </h2>
          <ul className="mt-10 divide-y divide-border/40 border-y border-border/40">
            {hours.map((h) => (
              <li
                key={h.day}
                data-testid={`hours-${h.day.toLowerCase()}`}
                className="grid grid-cols-[110px_1fr_auto] items-center py-4 gap-4"
              >
                <span className="text-[11px] tracking-luxe uppercase text-accent-line">{h.day}</span>
                <span className="text-sm text-foreground/85 font-sans">{h.service}</span>
                <Clock className="w-3.5 h-3.5 text-muted-foreground/70" strokeWidth={1.5} />
              </li>
            ))}
          </ul>
        </Reveal>

        <Reveal delay={0.15}>
          <SectionEyebrow>{t.socialTitle}</SectionEyebrow>
          <h2 className="font-serif text-4xl sm:text-5xl mt-5 italic" style={{ fontWeight: 400 }}>
            {t.socialHeading}
          </h2>
          <p className="mt-6 text-muted-foreground font-sans font-light max-w-md">
            {t.socialBlurb}
          </p>
          <div className="mt-8 space-y-4">
            {t.socialItems.map((s) => {
              const Icon = SOCIAL_ICONS[s.id] || ExternalLink;
              return (
                <SocialRow
                  key={s.id}
                  icon={Icon}
                  label={s.label}
                  handle={s.handle}
                  href={social[s.id] || "#"}
                  testid={`social-${s.id}`}
                />
              );
            })}
          </div>
        </Reveal>
      </section>

      {/* Map */}
      <section className="mt-20 md:mt-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 mb-8 flex flex-col md:flex-row md:items-end md:justify-between gap-4">
          <div>
            <SectionEyebrow>{t.mapEyebrow}</SectionEyebrow>
            <h2 className="font-serif text-4xl sm:text-5xl mt-5 italic" style={{ fontWeight: 400 }}>
              {t.mapTitle}
            </h2>
          </div>
          <a
            href={map.locationUrl}
            target="_blank"
            rel="noreferrer"
            data-testid="map-open-link"
            className="inline-flex items-center gap-3 text-[11px] tracking-luxe uppercase text-accent-line hover:gap-5 transition-all"
          >
            {t.mapOpenLabel} <ExternalLink className="w-4 h-4" strokeWidth={1.5} />
          </a>
        </div>
        <div className="relative h-[420px] md:h-[520px] w-full bg-card border-y border-border/40 overflow-hidden">
          <iframe
            data-testid="contact-map-iframe"
            title={t.mapAria}
            src={map.embedSrc}
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
            className="absolute inset-0 w-full h-full grayscale-[0.4] contrast-[1.05]"
            style={{ filter: "saturate(0.85) brightness(0.95)" }}
          />
        </div>
      </section>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────────────
function InfoCard({ icon: Icon, title, primary, secondary, ctaLabel, ctaHref, external, testid }) {
  return (
    <div
      data-testid={testid}
      className="group h-full p-8 bg-card/40 border border-border/60 hover:border-primary/60 transition-colors flex flex-col"
    >
      <span className="w-11 h-11 rounded-full border border-accent-line flex items-center justify-center text-accent-line">
        <Icon className="w-4 h-4" strokeWidth={1.5} />
      </span>
      <p className="mt-6 text-[10px] tracking-luxe uppercase text-muted-foreground">{title}</p>
      <p className="mt-2 font-serif text-2xl text-foreground italic" style={{ fontWeight: 500 }}>
        {primary}
      </p>
      <p className="text-sm text-muted-foreground mt-1 font-sans font-light">{secondary}</p>
      <a
        href={ctaHref}
        target={external ? "_blank" : undefined}
        rel={external ? "noreferrer" : undefined}
        className="mt-auto pt-8 inline-flex items-center gap-3 text-[11px] tracking-luxe uppercase text-accent-line group-hover:gap-5 transition-all"
      >
        {ctaLabel} <ExternalLink className="w-3.5 h-3.5" strokeWidth={1.5} />
      </a>
    </div>
  );
}

function SocialRow({ icon: Icon, label, handle, href, testid }) {
  return (
    <a
      href={href}
      data-testid={testid}
      target="_blank"
      rel="noreferrer"
      className="group flex items-center justify-between p-5 border border-border/50 hover:border-primary/60 transition-colors"
    >
      <div className="flex items-center gap-4">
        <span className="w-10 h-10 rounded-full border border-accent-line flex items-center justify-center text-accent-line group-hover:bg-burgundy dark:group-hover:bg-gold group-hover:text-white dark:group-hover:text-zinc-950 transition-colors">
          <Icon className="w-4 h-4" strokeWidth={1.5} />
        </span>
        <div>
          <p className="text-[10px] tracking-luxe uppercase text-muted-foreground">{label}</p>
          <p className="font-serif italic text-lg">{handle}</p>
        </div>
      </div>
      <ExternalLink className="w-4 h-4 text-muted-foreground group-hover:text-accent-line transition-colors" strokeWidth={1.5} />
    </a>
  );
}
